/**
 * A project on a phone.
 *
 * Four views, chosen by what you actually reach for away from a desk:
 *
 *  - Items    the outline. Which section, what's in it, what's done.
 *  - Board    the status kanban — one column at a time, cards moved with
 *             arrows rather than a drag no thumb can perform accurately.
 *  - Notes    the only thing on this screen that is CAPTURE rather than
 *             reference, and the one that is lost if it waits for a laptop.
 *  - About    client, dates, fee, scope, who to email, the documents. Read
 *             more or less only: attaching files and editing scope is desk
 *             work, and the client report lives at the bottom of it.
 *
 * The client report used to be one of only two choices here, which gave a
 * deliberate, sit-down artefact half the screen's navigation.
 *
 * Hours sit above all four, because "where are we on the budget" is the
 * question that gets asked while you are still in the room.
 */

import { useState } from "react";
import { useParams } from "react-router-dom";

import { Kanban, type Column } from "@/components/Kanban";
import { ItemRow } from "@/components/ItemRow";
import { SwipeRow } from "@/components/SwipeRow";
import { ProgressBar, card } from "@/components/atoms";
import { ClientReportTab, NotesTab } from "@/components/ProjectTabs";
import { useChrome } from "@/context/ChromeContext";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { useToast } from "@/context/ToastContext";
import {
  artifactHref, subtaskCounts, useAllSubtasks, useArtifacts, useContacts, useItemsForProject,
  usePhases, useProject, useProjects, useSetItemStatus, useToggleItem, useUpdateItem,
} from "@/hooks/useData";
import { useTimeEntries } from "@/hooks/useTime";
import { itemsById as buildItemsById } from "@/lib/deps";
import { buildHierarchy, listNesting } from "@/lib/hierarchy";
import { pct, plusWeek, shortDate } from "@/lib/format";
import { progressCounts } from "@/lib/hierarchy";
import { useShowCompleted } from "@/lib/theme";
import { feeLabel, hours } from "@/lib/timeRollup";
import {
  PROJECT_STATUS_LABEL, STATUS_META, STATUS_ORDER, type Item, type ItemStatus, type Project,
} from "@/lib/types";

import { Segmented } from "../components";
import { useLogSheet } from "../components/LogSheetContext";
import { mobileScreen } from "./MobileList";

type View = "items" | "board" | "notes" | "about";

export function MobileProject() {
  const { id } = useParams<{ id: string }>();
  const { data: project } = useProject(id);
  const { data: projects = [] } = useProjects();
  const { data: phases = [] } = usePhases(id);
  const { data: items = [] } = useItemsForProject(id);
  const { data: subs = [] } = useAllSubtasks();
  const { data: entries = [] } = useTimeEntries();
  const logSheet = useLogSheet();
  const toast = useToast();
  const toggle = useToggleItem();
  const updateItem = useUpdateItem();
  const setStatus = useSetItemStatus();
  const { openItem, openNew } = useTaskEditor();
  const [showCompleted, setShowCompleted] = useShowCompleted();
  const [view, setView] = useState<View>("items");

  const loggedSeconds = entries
    .filter((e) => e.project === id)
    .reduce((n, e) => n + (e.durationSeconds || 0), 0);
  const done = items.filter((i) => i.status === "done").length;
  const percent = pct(done, items.length);

  useChrome({
    title: project?.name || "Project",
    subtitle: `${done}/${items.length} done · ${percent}%`,
    back: { to: "/projects", label: "Projects" },
  }, [project?.name, done, items.length]);

  if (!project) {
    return <div style={{ ...mobileScreen, color: "var(--muted)", fontSize: 13 }}>Loading…</div>;
  }

  const projectById = new Map<string, Project>(projects.map((p) => [p.id, p]));
  const byId = buildItemsById(items);
  const subCounts = progressCounts(items, subtaskCounts(subs));
  const sortedPhases = [...phases].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const visible = items.filter((i) => showCompleted || i.status !== "done");

  return (
    <div style={mobileScreen}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "2px 2px 12px" }}>
        <span style={{ flex: 1 }}><ProgressBar value={percent} color={project.color} /></span>
        <span className="mono" style={{ fontSize: 11.5, color: "var(--muted2)" }}>{percent}%</span>
      </div>

      <TimeBand
        seconds={loggedSeconds}
        budgetHours={project.budgetHours ?? 0}
        color={project.color}
        onLog={() => logSheet.open({ projectId: project.id })}
      />

      {(view === "items" || view === "board") && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          {/* Same preference the desktop board and Settings use, so a project
              opened on either reads the same way. */}
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            style={{
              minHeight: 34, padding: "0 12px", borderRadius: 999, cursor: "pointer", fontSize: 12.5,
              border: `1px solid ${showCompleted ? "var(--accentLine)" : "var(--line2)"}`,
              background: showCompleted ? "var(--accentTint)" : "var(--surface)",
              color: showCompleted ? "var(--accent)" : "var(--muted)",
            }}
          >Completed {showCompleted ? "shown" : "hidden"}</button>
        </div>
      )}

      <div style={{ margin: "14px 0" }}>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: "items", label: "Items" },
            { value: "board", label: "Board" },
            { value: "notes", label: "Notes" },
            { value: "about", label: "About" },
          ]}
        />
      </div>

      {view === "items" && (
        <ItemsOutline
          project={project}
          phases={sortedPhases}
          items={items}
          projectById={projectById}
          byId={byId}
          subCounts={subCounts}
          showCompleted={showCompleted}
          onToggle={toggle}
          onOpen={openItem}
          onDefer={(it) => updateItem.mutate({ id: it.id, due: plusWeek(it.due) })}
          onAdd={(phaseId) => openNew(project.id, phaseId)}
        />
      )}

      {view === "board" && (
        <StatusBoard
          items={visible}
          allItems={items}
          phases={sortedPhases}
          subCounts={subCounts}
          showCompleted={showCompleted}
          onOpen={openItem}
          onToggle={toggle}
          onDefer={(it) => updateItem.mutate({ id: it.id, due: plusWeek(it.due) })}
          onSetStatus={(it, next) => setStatus.mutate({ id: it.id, status: next })}
          onRefuse={(message) => toast(message)}
        />
      )}

      {view === "notes" && <NotesTab projectId={project.id} mobile />}

      {view === "about" && (
        <AboutPanel project={project} phases={sortedPhases} items={items} />
      )}
    </div>
  );
}

/* -- Hours ------------------------------------------------------------------ */

function TimeBand({
  seconds, budgetHours, color, onLog,
}: {
  seconds: number;
  budgetHours: number;
  color: string;
  onLog: () => void;
}) {
  const logged = seconds / 3600;
  const percent = budgetHours > 0 ? Math.min(100, Math.round((logged / budgetHours) * 100)) : 0;
  const over = budgetHours > 0 && logged > budgetHours;

  return (
    <div style={{ ...card, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="mono" style={{ flex: 1, fontSize: 13.5, color: "var(--ink2)" }}>
          {hours(seconds)} h logged
          {budgetHours > 0 && <span style={{ color: over ? "var(--bad)" : "var(--muted2)" }}> / {budgetHours}h</span>}
        </span>
        <button
          onClick={onLog}
          style={{
            flex: "none", minHeight: 36, padding: "0 12px", borderRadius: 999, cursor: "pointer",
            border: "1px solid var(--accentLine)", background: "var(--accentTint)",
            color: "var(--accent)", fontSize: 13, fontWeight: 500,
          }}
        >Log time</button>
      </div>
      {budgetHours > 0 && (
        <div style={{ height: 4, borderRadius: 999, background: "var(--tint2)", overflow: "hidden", marginTop: 9 }}>
          <div style={{ height: "100%", width: `${percent}%`, background: over ? "var(--bad)" : color, borderRadius: 999 }} />
        </div>
      )}
    </div>
  );
}

/* -- Items ------------------------------------------------------------------ */

function ItemsOutline({
  project, phases, items, projectById, byId, subCounts, showCompleted,
  onToggle, onOpen, onDefer, onAdd,
}: {
  project: Project;
  phases: { id: string; name: string; color?: string; position?: number }[];
  items: Item[];
  projectById: Map<string, Project>;
  byId: Map<string, Item>;
  subCounts: Map<string, { done: number; total: number }>;
  showCompleted: boolean;
  onToggle: (i: Item) => void;
  onOpen: (i: Item) => void;
  onDefer: (i: Item) => void;
  onAdd: (phaseId: string) => void;
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  if (phases.length === 0) {
    return (
      <div style={{ padding: "40px 20px", textAlign: "center" }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>No phases yet</div>
        <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
          Phases are the structure of a project. Add the first one on a bigger screen.
        </div>
      </div>
    );
  }

  return (
    <>
      {phases.map((ph) => {
        const rows = items
          .filter((i) => i.phase === ph.id)
          .filter((i) => showCompleted || i.status !== "done")
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        // Todoist subtasks ride inside their parent's card, as on the board.
        const nest = listNesting(rows, items);
        const shut = collapsed[ph.id];
        const phDone = items.filter((i) => i.phase === ph.id && i.status === "done").length;
        const phTotal = items.filter((i) => i.phase === ph.id).length;

        return (
          <div key={ph.id} style={{ marginBottom: 18 }}>
            <button
              onClick={() => setCollapsed((c) => ({ ...c, [ph.id]: !c[ph.id] }))}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                padding: "8px 2px", border: "none", background: "none", cursor: "pointer",
                textAlign: "left", minHeight: 44,
              }}
            >
              <span style={{
                flex: "none", width: 9, height: 9, borderRadius: 3,
                background: ph.color || "var(--muted3)",
              }} />
              <span style={{
                flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: "var(--ink)",
                textTransform: "uppercase", letterSpacing: "0.04em",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{ph.name}</span>
              <span className="mono" style={{ flex: "none", fontSize: 11, color: "var(--muted2)" }}>
                {phDone}/{phTotal}
              </span>
              <span style={{ flex: "none", color: "var(--muted3)", fontSize: 12 }}>{shut ? "▸" : "▾"}</span>
            </button>

            {!shut && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {nest.top.map((it) => (
                    <SwipeRow
                      key={it.id}
                      onComplete={() => onToggle(it)}
                      onDefer={() => onDefer(it)}
                      completeLabel={it.status === "done" ? "Reopen" : "Complete"}
                    >
                      <ItemRow
                        item={it}
                        projectById={projectById}
                        onToggle={onToggle}
                        onOpen={onOpen}
                        showProject={false}
                        itemsById={byId}
                        subCounts={subCounts}
                        subtasks={nest.childrenOf(it)}
                        childrenOf={nest.childrenOf}
                        parent={nest.parentOf(it)}
                      />
                    </SwipeRow>
                  ))}
                </div>
                <button
                  onClick={() => onAdd(ph.id)}
                  style={{
                    width: "100%", marginTop: 8, minHeight: 44, borderRadius: 10,
                    border: "1px dashed var(--line2)", background: "none",
                    color: "var(--muted)", fontSize: 13, cursor: "pointer",
                  }}
                >
                  + Add in Todoist ↗
                </button>
              </>
            )}
          </div>
        );
      })}
    </>
  );
}

/* -- Board ------------------------------------------------------------------ */

/**
 * The status kanban, in the shape a phone can hold.
 *
 * `Kanban` has had a mobile mode all along — a scrolling chip row picks one
 * column and its cards render as a vertical list, with ← → on each card
 * instead of a drag. It was only ever reachable from the desktop project
 * screen, so the phone has never seen it.
 *
 * Grouping by phase is not offered: that is what Items already is, and a
 * toggle inside a tab inside a bucket is one nesting too many at this width.
 */
function StatusBoard({
  items, allItems, phases, subCounts, showCompleted, onOpen, onToggle, onDefer, onSetStatus, onRefuse,
}: {
  items: Item[];
  allItems: Item[];
  phases: { id: string; name: string; color?: string }[];
  subCounts: Map<string, { done: number; total: number }>;
  showCompleted: boolean;
  onOpen: (i: Item) => void;
  onToggle: (i: Item) => void;
  onDefer: (i: Item) => void;
  onSetStatus: (i: Item, next: ItemStatus) => void;
  onRefuse: (message: string) => void;
}) {
  const { parentOf } = buildHierarchy(allItems);
  const phaseById = new Map(phases.map((p) => [p.id, p]));

  const columns: Column[] = STATUS_ORDER
    .filter((s) => showCompleted || s !== "done")
    .map((s) => ({ key: s, name: STATUS_META[s].label, dot: STATUS_META[s].dot }));

  /**
   * Refuse to advance an item past `waiting` while what it depends on is still
   * open, and say why. Same rule as the desktop board — a dependency that only
   * holds on one screen is not a dependency.
   */
  function guard(it: Item, next: ItemStatus): boolean {
    const prereq = it.dependsOn ? allItems.find((x) => x.id === it.dependsOn) : undefined;
    if (prereq && prereq.status !== "done" && STATUS_ORDER.indexOf(next) > STATUS_ORDER.indexOf("waiting")) {
      onRefuse(`Waiting on "${prereq.title}" first`);
      return false;
    }
    return true;
  }

  function move(it: Item, dir: -1 | 1) {
    const idx = STATUS_ORDER.indexOf(it.status);
    const next = STATUS_ORDER[Math.min(STATUS_ORDER.length - 1, Math.max(0, idx + dir))];
    if (next === it.status || !guard(it, next)) return;
    onSetStatus(it, next);
  }

  return (
    <Kanban
      mobile
      items={items}
      columns={columns}
      columnOf={(i) => i.status}
      onDropIn={(i, s) => { if (guard(i, s as ItemStatus)) onSetStatus(i, s as ItemStatus); }}
      secondary={(i) => {
        const ph = phaseById.get(i.phase);
        return ph ? <PhaseChip name={ph.name} color={ph.color} /> : null;
      }}
      subCounts={subCounts}
      parentOf={parentOf}
      onComplete={onToggle}
      onDefer={onDefer}
      onToggle={onToggle}
      onOpen={onOpen}
      onMove={move}
      compare={(a, b) => (a.position ?? 0) - (b.position ?? 0)}
    />
  );
}

function PhaseChip({ name, color }: { name: string; color?: string }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, maxWidth: "100%",
      fontSize: 11, color: "var(--muted)",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color || "var(--muted3)", flex: "none" }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
    </span>
  );
}

/* -- About ------------------------------------------------------------------ */

/**
 * The reference card: who, when, for how much, and the documents.
 *
 * Read-only on purpose. The desktop Overview is also where you attach files,
 * add contacts and record scope changes — none of which is phone work, and all
 * of which would bury the four facts you actually came to check.
 */
function AboutPanel({
  project, phases, items,
}: {
  project: Project;
  phases: { id: string; name: string; color?: string }[];
  items: Item[];
}) {
  const { data: contacts = [] } = useContacts(project.id);
  const { data: artifacts = [] } = useArtifacts(project.id);
  const [report, setReport] = useState(false);

  const facts: [string, string][] = [
    ["Client", project.client || "Internal"],
    ["Status", PROJECT_STATUS_LABEL[project.status] ?? project.status],
    ["Start", shortDate(project.start) || "—"],
    ["Due", shortDate(project.due) || "—"],
    ["Fee", feeLabel(project) || "—"],
    ["Contract", project.contract || "—"],
  ];

  return (
    <>
      <div style={{ ...card, padding: "4px 14px" }}>
        {facts.map(([label, value]) => (
          <div key={label} style={{
            display: "flex", alignItems: "baseline", gap: 12, padding: "10px 0",
            borderTop: label === "Client" ? "none" : "1px solid var(--tint2)",
          }}>
            <span style={{ flex: "none", width: 76, fontSize: 12.5, color: "var(--muted)" }}>{label}</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "var(--ink)" }}>{value}</span>
          </div>
        ))}
      </div>

      {project.scope && (
        <div style={{ ...card, padding: 14, marginTop: 10 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--muted2)", marginBottom: 6 }}>Scope</div>
          <div className="prose" style={{ fontSize: 13.5, color: "var(--ink2)", whiteSpace: "pre-wrap" }}>{project.scope}</div>
        </div>
      )}

      {contacts.length > 0 && (
        <div style={{ ...card, padding: "4px 14px", marginTop: 10 }}>
          {contacts.map((c, i) => (
            <div key={c.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 0", minHeight: 52,
              borderTop: i === 0 ? "none" : "1px solid var(--tint2)",
            }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 14, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                {c.role && <span style={{ display: "block", fontSize: 12, color: "var(--muted)" }}>{c.role}</span>}
              </span>
              {/* A tap, not a copy-and-paste into Mail. There is no phone
                  number on a contact yet, or this would offer a call too. */}
              {c.email && (
                <a
                  href={`mailto:${c.email}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    flex: "none", minHeight: 36, padding: "0 12px", borderRadius: 999,
                    display: "inline-flex", alignItems: "center",
                    border: "1px solid var(--line2)", background: "var(--surface)",
                    color: "var(--accent)", fontSize: 12.5, textDecoration: "none",
                  }}
                >Email</a>
              )}
            </div>
          ))}
        </div>
      )}

      {artifacts.length > 0 && (
        <div style={{ ...card, padding: "4px 14px", marginTop: 10 }}>
          {artifacts.map((a, i) => {
            const href = artifactHref(a);
            return (
              <a
                key={a.id}
                href={href}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "11px 0", minHeight: 52,
                  borderTop: i === 0 ? "none" : "1px solid var(--tint2)",
                  textDecoration: "none", color: "var(--ink)",
                  opacity: href ? 1 : 0.5, pointerEvents: href ? "auto" : "none",
                }}
              >
                <span className="mono" style={{ flex: "none", fontSize: 10.5, color: "var(--muted2)" }}>{a.kind || "Link"}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.label}</span>
                <span aria-hidden style={{ flex: "none", color: "var(--muted2)" }}>↗</span>
              </a>
            );
          })}
        </div>
      )}

      {/* Kept, but no longer half the screen's navigation: assembling a status
          for a client is something you sit down to do. */}
      <button
        onClick={() => setReport((v) => !v)}
        style={{
          width: "100%", marginTop: 14, minHeight: 48, borderRadius: 10, cursor: "pointer",
          border: "1px solid var(--line2)", background: "var(--surface)",
          color: "var(--ink2)", fontSize: 13.5,
        }}
      >{report ? "Hide client report" : "Client report"}</button>

      {report && (
        <div style={{ marginTop: 14 }}>
          <ClientReportTab project={project} phases={phases as never} items={items} mobile />
        </div>
      )}
    </>
  );
}
